import { FILTER_MAP } from '../constants.js';
import { advancedFilterSessions } from '../utils/filters.js';
import { sortSubsessions } from '../utils/sort-subsessions.js';

/**
 * Get the available filters for the schedule
 * @param {Object} filterOptions - The filter options
 * @param {Array} embedFields - The embed fields
 * @returns {Object} The available filters
 */
const getAvailableFilters = (filterOptions, embedFields) => {
  if (!filterOptions || !embedFields) {
    return {};
  }

  const filters = {};

  // Build available filters
  Object.entries(FILTER_MAP).forEach(([filterKey, internalName]) => {
    // Check if this filter is enabled in filterOptions
    if (!filterOptions[filterKey]) return;

    // Find the corresponding embed field
    const embedField = embedFields.find(field => field.internal_name === internalName);
    if (
      !embedField ||
      !embedField.settings ||
      !embedField.settings.options ||
      embedField.settings.options.length === 0
    ) {
      return; // Skip if no options available
    }

    filters[internalName] = {
      name: embedField.name,
      options: embedField.settings.options,
      allowMultiple: embedField.settings.allowMultipleSelect || false,
    };
  });

  return filters;
};

/**
 * generate a function that filters sessions based on search query and active filters
 * @param {Object} activeFilters - The active filters to apply to the sessions
 * @param {string} searchQuery - The search query to apply to the sessions
 * @param {Object} availableFilters - Filter configs from getAvailableFilters
 * @param {string[]} searchFields - The session fields to search in (from buildSessionSearchFields)
 * @returns {Function} A function that filters sessions based on the active filters and search query
 */
const filterSessionGeneratorFn =
  (activeFilters = {}, searchQuery = '', availableFilters = {}, searchFields = []) =>
  sessions => {
    return advancedFilterSessions(sessions, searchQuery, activeFilters, availableFilters, {
      searchFields,
      fieldMappingStyle: 'id',
    });
  };

/**
 * Get the data for the current day
 * @param {Array} orderedDateKeys - The ordered date keys
 * @param {Object} tablesByDay - The tables by day
 * @param {number} selectedDay - The selected day
 * @param {Function} filterSessionFn - The function to filter the sessions
 * @returns {Object} The data for the current day
 */
const getCurrentDayData = (orderedDateKeys, tablesByDay, selectedDay, filterSessionFn) => {
  if (!orderedDateKeys || !tablesByDay) {
    return null;
  }

  const dayKey = orderedDateKeys[selectedDay];
  const dayData = tablesByDay[dayKey];

  if (!dayData) return null;

  // Apply filtering to sessions
  const filteredSessionsByTime = dayData.table.sessionsByTime
    .map(timeSlot => {
      const sessionsInTime = (timeSlot?.timeItems || []).map(timeItem => timeItem.session);
      const filteredTimeItems = filterSessionFn(sessionsInTime);
      return {
        ...timeSlot,
        timeItems: filteredTimeItems,
      };
    })
    .filter(timeSlot => timeSlot.timeItems.length > 0);

  return {
    ...dayData,
    table: {
      ...dayData.table,
      sessionsByTime: filteredSessionsByTime,
    },
  };
};

/**
 * Pre-filter all days and sessions based on search/filter criteria for optimal performance
 * @param {Array} orderedDateKeys - The ordered date keys (dayTabs)
 * @param {Object} tablesByDay - The tables by day containing session data
 * @param {Function} filterSessionFn - The function to filter sessions
 * @returns {Object} Object containing filteredDayTabs and filteredTablesByDay
 */
const getFilteredScheduleData = (orderedDateKeys, tablesByDay, filterSessionFn) => {
  if (!orderedDateKeys || !tablesByDay || !filterSessionFn) {
    return {
      filteredDayTabs: orderedDateKeys || [],
      filteredTablesByDay: tablesByDay || {},
    };
  }

  const filteredTablesByDay = {};
  const filteredDayTabs = [];

  // Process each day and filter its sessions
  orderedDateKeys.forEach(dayKey => {
    const dayData = tablesByDay[dayKey];

    if (!dayData?.table?.sessionsByTime) {
      return;
    }

    // Filter sessions for this day
    const filteredSessionsByTime = dayData.table.sessionsByTime
      .map(timeSlot => {
        if (!timeSlot?.timeItems?.length) {
          return null;
        }

        // Extract sessions from timeItems and apply filter
        const sessionsInTime = timeSlot.timeItems.map(timeItem => timeItem.session);
        const filteredTimeItems = filterSessionFn(sessionsInTime);

        return filteredTimeItems.length > 0
          ? {
              ...timeSlot,
              timeItems: filteredTimeItems,
            }
          : null;
      })
      .filter(timeSlot => timeSlot !== null);

    // Only include day if it has sessions after filtering
    if (filteredSessionsByTime.length > 0) {
      filteredDayTabs.push(dayKey);
      filteredTablesByDay[dayKey] = {
        ...dayData,
        table: {
          ...dayData.table,
          sessionsByTime: filteredSessionsByTime,
        },
      };
    }
  });

  return {
    filteredDayTabs,
    filteredTablesByDay,
  };
};

/**
 * Get the data for the current day from pre-filtered data
 * @param {Array} filteredDayTabs - The filtered day tabs
 * @param {Object} filteredTablesByDay - The pre-filtered tables by day
 * @param {number} selectedDay - The selected day index
 * @returns {Object} The data for the current day (already filtered)
 */
const getCurrentDayDataFromFiltered = (filteredDayTabs, filteredTablesByDay, selectedDay) => {
  if (!filteredDayTabs || !filteredTablesByDay) {
    return null;
  }

  const dayKey = filteredDayTabs[selectedDay];
  return filteredTablesByDay[dayKey] || null;
};

/**
 * Get the total count of all filtered sessions across all days
 * @param {Array} filteredDayTabs - The filtered day tabs
 * @param {Object} filteredTablesByDay - The pre-filtered tables by day
 * @returns {number} Total count of sessions across all filtered days
 */
const getAllFilteredSessionsCount = (filteredDayTabs, filteredTablesByDay) => {
  if (!filteredDayTabs || !filteredTablesByDay) {
    return 0;
  }

  let totalCount = 0;

  filteredDayTabs.forEach(dayKey => {
    const dayData = filteredTablesByDay[dayKey];
    if (dayData?.table?.sessionsByTime) {
      dayData.table.sessionsByTime.forEach(timeSlot => {
        totalCount += timeSlot.timeItems?.length || 0;
      });
    }
  });

  return totalCount;
};

/**
 * Transform session data for SessionModal using sessionsById
 * @param {string} sessionId - The session ID to get data for
 * @param {Object} sessionsById - Dictionary of sessions by ID
 * @param {Object} speakersById - Dictionary of speakers by ID
 * @returns {Object|null} Transformed session data for modal or null if not found
 */
const getSessionDataForModal = (sessionId, sessionsById = {}, speakersById = {}) => {
  const session = sessionsById[sessionId];

  if (!session) return null;

  return {
    ...session,
    id: session.id,
    title: session.title,
    description: session.description,
    date: session.range,
    location: session.location,
    track: session.track,
    format: session.format,
    clientId: session.id,
    ceuCredits: session.ceu_credits,
    tags: session.tags?.map(tag => tag.name).join(', ') || '',
    // SB-8942: the modal renders `tagsList` as a dot list; without it the
    // schedule widget was the one surface still showing joined tag names.
    tagsList: session.tags?.map(tag => tag.name) || [],
    level: session.level,
    language: session.language,
    speakers:
      session.speakers?.map(speaker => ({
        id: speaker.id,
        name: speaker.full_name,
        salutation: speakersById[speaker.id]?.salutation || speaker.salutation || '',
        honorific: speakersById[speaker.id]?.honorific || speaker.honorific || '',
        title: speakersById[speaker.id]?.title || '',
        company: speakersById[speaker.id]?.company_name || '',
        photo: speakersById[speaker.id]?.photo || '',
        // SB-9832: present only when the org shows current disclosures on embeds.
        disclosure: speakersById[speaker.id]?.disclosure || speaker.disclosure || null,
        role: speaker.role || '',
        // SB-8853: slug-grouped roles with organizer labels for custom roles.
        role_slug: speaker.role_slug || speaker.role || 'speaker',
        role_label: speaker.role_label || null,
        role_label_plural: speaker.role_label_plural || null,
      })) || [],
    // SB-9083: admin order everywhere subsessions are listed, not just in the modal.
    subsessions: sortSubsessions(session.subsessions || []),
    // Calendar data if available
    endTimeAddEvent: session.end_time_addEvent,
    endsAtAddEvent: session.ends_at_addEvent,
    startTimeAddEvent: session.start_time_addEvent,
    startsAtAddEvent: session.starts_at_addEvent,
    timezone: session.timezone,
  };
};

/**
 * Get speaker data for SpeakerModal using speakersById
 * @param {string} speakerId - The speaker ID to get data for
 * @param {Object} speakersById - Dictionary of speakers by ID
 * @param {Object} sessionsById - Dictionary of sessions by ID
 * @returns {Object|null} Speaker data for modal or null if not found
 */
const getSpeakerDataForModal = (speakerId, speakersById = {}, sessionsById = {}) => {
  const speaker = speakersById[speakerId];
  if (!speaker) return null;
  return {
    ...speaker,
    sessions: (speaker?.sessions || []).map(session => sessionsById[session.id]),
  };
};

const getEnhancedSessionForCalendar = (sessionId, sessionsById = {}) => {
  const session = sessionsById[sessionId];
  if (!session) return null;
  return {
    title: session.title,
    description: session.description,
    location: session.location,
    timezone: session.timezone,
    startTimeAddEvent: session.start_time_addEvent,
    endTimeAddEvent: session.end_time_addEvent,
    endsAtAddEvent: session.ends_at_addEvent,
    startsAtAddEvent: session.starts_at_addEvent,
  };
};

export {
  getAvailableFilters,
  filterSessionGeneratorFn,
  getCurrentDayData,
  getFilteredScheduleData,
  getCurrentDayDataFromFiltered,
  getAllFilteredSessionsCount,
  getSessionDataForModal,
  getSpeakerDataForModal,
  getEnhancedSessionForCalendar,
};
